import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Upload as TusUpload } from "tus-js-client";
import api, { formatApiError } from "@/lib/api";
import StudioLayout from "@/components/layouts/StudioLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle,
} from "@/components/ui/drawer";
import {
  Loader2, ArrowLeft, Trash2, Plus, Pencil, ExternalLink, BarChart3, Lock,
  Image as ImageIcon, Calendar as CalendarIcon, Subtitles, Tv, Sparkles, Film,
  UploadCloud, CheckCircle2, X as XIcon,
} from "lucide-react";
import { toast } from "sonner";
import { formatMoney, smallestUnitToMajor, majorToSmallestUnit, currencySymbol } from "@/lib/money";

const KIND_OPTIONS = ["movie", "series", "reel"];
const AGE_RATINGS = ["U", "U/A 7+", "U/A 13+", "U/A 16+", "A"];

const empty = {
  kind: "movie",
  title: "", slug: "", description: "",
  thumbnail_url: "", hero_image_url: "",
  poster_portrait_url: "", hero_banner_url: "",
  trailer_url: "",
  genres: "", languages: "English",
  age_rating: "U", release_year: 2024, duration_minutes: "",
  cast: "", tags: "",
  status: "draft", featured: false,
  subscription_included: true,
  publish_at: "",
  video_url: "",
  cf_uid: "",
  creator_name: "", linked_content_id: "",
};

function toForm(item) {
  return {
    kind: item.kind,
    title: item.title, slug: item.slug,
    description: item.description || "",
    thumbnail_url: item.thumbnail_url || "",
    hero_image_url: item.hero_image_url || "",
    poster_portrait_url: item.poster_portrait_url || "",
    hero_banner_url: item.hero_banner_url || "",
    trailer_url: item.trailer_url || "",
    genres: (item.genres || []).join(", "),
    languages: (item.languages || []).join(", "),
    age_rating: item.age_rating || "U",
    release_year: item.release_year || "",
    duration_minutes: item.duration_minutes ?? "",
    cast: (item.cast || []).join(", "),
    tags: (item.tags || []).join(", "),
    status: item.status,
    featured: !!item.featured,
    subscription_included: item.subscription_included !== false,
    publish_at: item.publish_at ? item.publish_at.slice(0, 16) : "",
    video_url: item.video_source?.type === "cloudflare" ? "" : (item.video_source?.original_url || ""),
    cf_uid: item.video_source?.type === "cloudflare" ? (item.video_source.uid || "") : "",
    creator_name: item.creator_name || "",
    linked_content_id: item.linked_content_id || "",
  };
}

function fromForm(f) {
  const out = {
    title: f.title.trim(),
    description: f.description,
    thumbnail_url: f.thumbnail_url.trim(),
    hero_image_url: f.hero_image_url.trim(),
    poster_portrait_url: f.poster_portrait_url.trim(),
    hero_banner_url: f.hero_banner_url.trim(),
    trailer_url: f.trailer_url.trim() || null,
    genres: splitList(f.genres),
    languages: splitList(f.languages),
    age_rating: f.age_rating,
    release_year: f.release_year ? parseInt(f.release_year, 10) : null,
    duration_minutes: f.duration_minutes !== "" ? parseInt(f.duration_minutes, 10) : null,
    cast: splitList(f.cast),
    tags: splitList(f.tags),
    status: f.status,
    featured: !!f.featured,
    subscription_included: !!f.subscription_included,
  };
  if (f.slug && f.slug.trim()) out.slug = f.slug.trim().toLowerCase();
  return out;
}

function splitList(s) {
  return (s || "").split(",").map((x) => x.trim()).filter(Boolean);
}

function StreamUploader({ cfUid, onUploaded, onClear }) {
  const [phase, setPhase] = useState("idle"); // idle | uploading | processing | ready | error
  const [pct, setPct] = useState(0);
  const [msg, setMsg] = useState("");
  const [replacing, setReplacing] = useState(false); // showing uploader to swap an existing video

  const pollStatus = async (uid) => {
    setPhase("processing");
    for (let i = 0; i < 120; i++) {
      try {
        const { data } = await api.get(`/cms/stream/${uid}/status`);
        if (data.ready) { setPhase("ready"); return; }
        if (data.pct != null) setMsg(`Encoding… ${data.pct}%`);
      } catch (_e) { /* keep polling */ }
      await new Promise((r) => setTimeout(r, 4000));
    }
    setPhase("ready"); // assume ready after timeout; playback URL still works once done
  };

  const onFile = async (file) => {
    if (!file) return;
    setPhase("uploading"); setPct(0); setMsg("");
    try {
      const { data } = await api.post("/cms/stream/direct-upload", { size: file.size, filename: file.name });
      const upload = new TusUpload(file, {
        uploadUrl: data.upload_url,
        chunkSize: 50 * 1024 * 1024,
        retryDelays: [0, 3000, 6000, 12000],
        metadata: { name: file.name },
        onProgress: (sent, total) => setPct(Math.round((sent / total) * 100)),
        onError: (err) => { setPhase("error"); setMsg(String(err).slice(0, 160)); },
        // Swap in the new video only after the upload succeeds — the old one
        // stays attached until this moment, so views/analytics are never lost.
        onSuccess: () => { setReplacing(false); onUploaded(data.uid); pollStatus(data.uid); },
      });
      upload.start();
    } catch (e) {
      setPhase("error");
      setMsg(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  // Has an uploaded video attached AND we're not mid-replace → show current + actions
  if (cfUid && !replacing && phase !== "uploading" && phase !== "error") {
    return (
      <div className="border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="stream-uploaded">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
            <div className="min-w-0">
              <div className="text-sm text-white font-medium">
                {phase === "processing" ? "Uploaded — encoding…" : "Current video"}
              </div>
              <div className="text-[11px] text-white/40 font-mono truncate">Cloudflare Stream · {cfUid}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => { setReplacing(true); setPhase("idle"); setMsg(""); setPct(0); }}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-white text-black text-[10px] font-semibold uppercase tracking-[0.15em] hover:bg-amber-300 transition-colors"
              data-testid="stream-replace"
            >
              <UploadCloud className="w-3.5 h-3.5" /> Replace video
            </button>
            <button
              type="button"
              onClick={() => { setPhase("idle"); onClear(); }}
              className="text-white/40 hover:text-red-400 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest"
              data-testid="stream-remove"
            >
              <XIcon className="w-3.5 h-3.5" /> Remove
            </button>
          </div>
        </div>
        <p className="text-[11px] text-white/40 mt-2">Replacing swaps the video for this episode — the episode, its views &amp; analytics are kept.</p>
      </div>
    );
  }

  return (
    <div>
      <label className="inline-flex flex-col items-start cursor-pointer" data-testid="stream-dropzone">
        <input
          type="file"
          accept="video/*"
          className="hidden"
          disabled={phase === "uploading" || phase === "processing"}
          onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
          data-testid="stream-file-input"
        />
        {phase === "uploading" ? (
          <div className="w-full max-w-sm space-y-2">
            <div className="inline-flex items-center gap-2 px-5 py-3 bg-white/10 text-white text-sm font-semibold uppercase tracking-widest">
              <UploadCloud className="w-4 h-4" /> Uploading… {pct}%
            </div>
            <div className="h-1.5 bg-white/10 overflow-hidden">
              <div className="h-full bg-white transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
        ) : (
          <span className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black text-xs font-semibold uppercase tracking-[0.2em] hover:bg-white/90 transition-colors" data-testid="stream-upload-button">
            <UploadCloud className="w-4 h-4" /> {replacing ? "Upload new video" : "Upload video"}
          </span>
        )}
      </label>
      {replacing && phase !== "uploading" && (
        <button
          type="button"
          onClick={() => { setReplacing(false); setPhase("idle"); setMsg(""); }}
          className="ml-3 text-white/50 hover:text-white text-[10px] uppercase tracking-widest"
          data-testid="stream-replace-cancel"
        >
          Cancel
        </button>
      )}
      <p className="text-xs text-white/40 mt-2">MP4/MOV/MKV — streamed adaptively via Cloudflare with your own branding. Max 30&nbsp;GB.</p>
      {msg && <p className={`text-xs mt-1 ${phase === "error" ? "text-rose-300" : "text-white/50"}`} data-testid="stream-upload-msg">{msg}</p>}
    </div>
  );
}


function youtubeIdFromUrl(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
}

export default function StudioCatalogueEditor() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [item, setItem] = useState(null);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [otherItems, setOtherItems] = useState([]);
  const [insightsOpen, setInsightsOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    const tasks = [api.get("/tenant/me"), api.get("/cms/content")];
    if (!isNew) tasks.push(api.get(`/cms/content/${id}`));
    Promise.all(tasks)
      .then((res) => {
        if (!alive) return;
        setTenant(res[0].data);
        setOtherItems(res[1].data);
        if (!isNew) {
          setItem(res[2].data);
          setForm(toForm(res[2].data));
        }
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || e.message));
    return () => { alive = false; };
  }, [id, isNew]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const kind = isNew ? form.kind : item?.kind;
  const movieSlug = (otherItems.find((o) => o.id === form.linked_content_id) || {}).slug;
  const trailerYtId = useMemo(() => youtubeIdFromUrl(form.trailer_url), [form.trailer_url]);
  const monetizationWarning = !form.subscription_included && !form.tvod_rent_price && !form.tvod_buy_price &&
    !item?.tvod_rent_price && !item?.tvod_buy_price;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const payload = fromForm(form);
      if (form.publish_at) payload.publish_at = new Date(form.publish_at).toISOString();
      if (isNew) {
        payload.kind = form.kind;
        if (form.kind === "movie" || form.kind === "reel") {
          if (form.cf_uid) payload.cf_uid = form.cf_uid;
          else payload.video_url = form.video_url.trim();
        }
        if (form.kind === "reel") {
          payload.creator_name = form.creator_name;
          if (form.linked_content_id) payload.linked_content_id = form.linked_content_id;
        }
        const { data } = await api.post("/cms/content", payload);
        toast.success(`${data.title} created`);
        navigate(`/studio/catalogue/${data.id}`, { replace: true });
      } else {
        if (item.kind === "movie" || item.kind === "reel") {
          if (form.cf_uid) payload.cf_uid = form.cf_uid;
          else payload.video_url = form.video_url.trim();
        }
        if (item.kind === "reel") {
          payload.creator_name = form.creator_name;
          payload.linked_content_id = form.linked_content_id || null;
        }
        const { data } = await api.patch(`/cms/content/${id}`, payload);
        setItem(data);
        setForm(toForm(data));
        toast.success("Saved");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!tenant) {
    return (
      <StudioLayout tenant={tenant}>
        <div className="text-white/50 text-sm uppercase tracking-widest" data-testid="editor-loading">Loading…</div>
      </StudioLayout>
    );
  }

  return (
    <StudioLayout tenant={tenant}>
      <div className="flex items-center gap-3 mb-6">
        <Link to="/studio/catalogue" className="text-white/50 hover:text-white inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.3em]" data-testid="editor-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to catalogue
        </Link>
      </div>
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.4em] text-white/40">{isNew ? "New title" : `Editing · ${item?.kind}`}</p>
          <h1 className="mt-2 font-display font-bold text-3xl md:text-4xl tracking-tight" data-testid="editor-title">
            {isNew ? "Add a title" : item?.title}
          </h1>
          {!isNew && (
            <div className="mt-2 flex items-center gap-3">
              <span className={`inline-flex items-center px-2 py-0.5 border text-[10px] uppercase tracking-[0.25em] ${
                item.status === "published" ? "bg-emerald-500/15 text-emerald-300 border-emerald-400/30" :
                item.status === "scheduled" ? "bg-amber-500/15 text-amber-300 border-amber-400/30" :
                item.status === "archived" ? "bg-zinc-500/15 text-zinc-400 border-zinc-400/30" :
                "bg-slate-500/15 text-slate-300 border-slate-400/30"
              }`}>{item.status}</span>
              <Link to={`/s/${tenant.slug}/title/${item.slug}`} target="_blank" rel="noreferrer" className="text-xs text-white/50 hover:text-white inline-flex items-center gap-1 uppercase tracking-widest">
                View on storefront <ExternalLink className="w-3 h-3" />
              </Link>
            </div>
          )}
        </div>
        {!isNew && (
          <Button
            onClick={() => setInsightsOpen(true)}
            variant="ghost"
            className="rounded-none h-10 text-white/70 hover:text-white border border-white/15 px-3 uppercase text-[11px] tracking-[0.25em]"
            data-testid="editor-insights-button"
          >
            <BarChart3 className="w-4 h-4 mr-1.5" /> Insights
          </Button>
        )}
      </div>

      <form onSubmit={onSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="editor-form">
        <div className="lg:col-span-2 bg-[#121212] border border-white/10 p-6">
          <Tabs defaultValue="basics" className="w-full">
            <TabsList className="bg-[#0a0a0a] border border-white/10 rounded-none p-1 h-auto flex flex-wrap gap-1 mb-6">
              <TabsTrigger value="basics" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black uppercase text-[11px] tracking-[0.25em] px-3 py-2" data-testid="editor-tab-basics">Basics</TabsTrigger>
              <TabsTrigger value="artwork" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black uppercase text-[11px] tracking-[0.25em] px-3 py-2" data-testid="editor-tab-artwork">Artwork</TabsTrigger>
              <TabsTrigger value="video" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black uppercase text-[11px] tracking-[0.25em] px-3 py-2" data-testid="editor-tab-video">Video</TabsTrigger>
              <TabsTrigger value="subtitles" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black uppercase text-[11px] tracking-[0.25em] px-3 py-2" data-testid="editor-tab-subtitles">Subtitles</TabsTrigger>
              <TabsTrigger value="monetization" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black uppercase text-[11px] tracking-[0.25em] px-3 py-2" data-testid="editor-tab-monetization">Monetization</TabsTrigger>
              <TabsTrigger value="schedule" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black uppercase text-[11px] tracking-[0.25em] px-3 py-2" data-testid="editor-tab-schedule">Schedule</TabsTrigger>
            </TabsList>

            {/* Basics */}
            <TabsContent value="basics" className="space-y-5 mt-0">
              {isNew && (
                <Field label="Kind" hint="Decide first — kind cannot be changed later">
                  <Select value={form.kind} onValueChange={(v) => set("kind", v)}>
                    <SelectTrigger className="bg-transparent border-white/15 rounded-none" data-testid="editor-kind"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10 rounded-none text-white">
                      {KIND_OPTIONS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              )}
              <Field label="Title">
                <Input value={form.title} onChange={(e) => set("title", e.target.value)} required className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="editor-title-input" />
              </Field>
              <Field label="Slug" hint="Auto-generated from title if blank.">
                <Input value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="auto" className="bg-transparent border-white/15 rounded-none focus-visible:border-white font-mono" data-testid="editor-slug" />
              </Field>
              <Field label="Description">
                <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="editor-description" />
              </Field>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Release year">
                  <Input type="number" value={form.release_year} onChange={(e) => set("release_year", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="editor-year" />
                </Field>
                {kind !== "series" && (
                  <Field label="Duration (min)">
                    <Input type="number" value={form.duration_minutes} onChange={(e) => set("duration_minutes", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="editor-duration" />
                  </Field>
                )}
                <Field label="Age rating">
                  <Select value={form.age_rating} onValueChange={(v) => set("age_rating", v)}>
                    <SelectTrigger className="bg-transparent border-white/15 rounded-none" data-testid="editor-age"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10 rounded-none text-white">
                      {AGE_RATINGS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Genres" hint="Comma-separated, e.g. Drama, Sci-Fi"><Input value={form.genres} onChange={(e) => set("genres", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="editor-genres" /></Field>
              <Field label="Languages" hint="Comma-separated"><Input value={form.languages} onChange={(e) => set("languages", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="editor-languages" /></Field>
              <Field label="Cast" hint="Comma-separated"><Input value={form.cast} onChange={(e) => set("cast", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="editor-cast" /></Field>
              <Field label="Tags" hint="Comma-separated"><Input value={form.tags} onChange={(e) => set("tags", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="editor-tags" /></Field>
            </TabsContent>

            {/* Artwork */}
            <TabsContent value="artwork" className="space-y-5 mt-0">
              <div className="border border-white/10 bg-black/30 p-3 flex items-start gap-3">
                <ImageIcon className="w-4 h-4 mt-0.5 text-white/45" />
                <p className="text-xs text-white/55 font-light">Upload an image or paste a URL. Recommended: landscape 16:9 thumbnail, portrait 2:3 poster, wide 21:9 hero banner.</p>
              </div>
              {form.cf_uid && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const { data } = await api.get(`/cms/stream/${form.cf_uid}/status`);
                      if (data.thumbnail) {
                        set("thumbnail_url", data.thumbnail);
                        toast.success("Applied the video's auto-generated thumbnail");
                      } else {
                        toast.error("Thumbnail not ready yet — try again shortly");
                      }
                    } catch (e) {
                      toast.error("Couldn't fetch the video thumbnail");
                    }
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-white/80 hover:bg-white/10 text-[11px] uppercase tracking-[0.2em]"
                  data-testid="use-video-thumb"
                >
                  <ImageIcon className="w-3.5 h-3.5" /> Use the video's thumbnail
                </button>
              )}
              <ArtField label="Thumbnail URL · landscape 16:9" testid="editor-thumb" aspect="aspect-video" value={form.thumbnail_url} onChange={(v) => set("thumbnail_url", v)} />
              <ArtField label="Poster URL · portrait 2:3" testid="editor-poster" aspect="aspect-[2/3] max-w-[200px]" value={form.poster_portrait_url} onChange={(v) => set("poster_portrait_url", v)} />
              <ArtField label="Hero banner URL · wide 21:9" testid="editor-hero" aspect="aspect-[21/9]" value={form.hero_image_url || form.hero_banner_url} onChange={(v) => { set("hero_banner_url", v); set("hero_image_url", v); }} />
            </TabsContent>

            {/* Video */}
            <TabsContent value="video" className="space-y-5 mt-0">
              {(kind === "movie" || kind === "reel") ? (
                <StreamUploader
                  cfUid={form.cf_uid}
                  onUploaded={(uid) => { set("cf_uid", uid); set("video_url", ""); }}
                  onClear={() => set("cf_uid", "")}
                />
              ) : (
                <div className="border border-dashed border-white/15 p-8 text-center text-white/45 text-sm">
                  Series episodes carry their own video. Add episodes below.
                </div>
              )}
              <Field label="Trailer URL" hint="Optional — separate trailer (YT or Vimeo).">
                <Input value={form.trailer_url} onChange={(e) => set("trailer_url", e.target.value)} placeholder="https://www.youtube.com/watch?v=..." className="bg-transparent border-white/15 rounded-none focus-visible:border-white font-mono text-sm" data-testid="editor-trailer-url" />
              </Field>
              {trailerYtId && (
                <div className="aspect-video bg-black border border-white/10 overflow-hidden">
                  <img src={`https://img.youtube.com/vi/${trailerYtId}/hqdefault.jpg`} alt="Trailer preview" className="w-full h-full object-cover" />
                </div>
              )}
              {kind === "reel" && (
                <>
                  <Field label="Creator name">
                    <Input value={form.creator_name} onChange={(e) => set("creator_name", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="editor-creator" />
                  </Field>
                  <Field label="Linked title" hint='Show "Watch full film" CTA on the reel.'>
                    <Select value={form.linked_content_id || "none"} onValueChange={(v) => set("linked_content_id", v === "none" ? "" : v)}>
                      <SelectTrigger className="bg-transparent border-white/15 rounded-none" data-testid="editor-linked"><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/10 rounded-none text-white max-h-72">
                        <SelectItem value="none">None</SelectItem>
                        {otherItems.filter((o) => o.kind !== "reel").map((o) => (
                          <SelectItem key={o.id} value={o.id}>{o.title} <span className="text-white/40">— {o.kind}</span></SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {movieSlug && <div className="text-[10px] text-white/40 mt-1">→ {movieSlug}</div>}
                  </Field>
                </>
              )}
            </TabsContent>

            {/* Subtitles */}
            <TabsContent value="subtitles" className="space-y-5 mt-0">
              {!isNew ? (
                <SubtitlesEditor item={item} onChanged={(updated) => { setItem(updated); setForm(toForm(updated)); }} />
              ) : (
                <div className="border border-dashed border-white/15 p-8 text-center text-white/45 text-sm">
                  Save the title first, then add subtitle tracks.
                </div>
              )}
            </TabsContent>

            {/* Monetization */}
            <TabsContent value="monetization" className="space-y-5 mt-0">
              <div className="border border-white/10 bg-[#0a0a0a] p-4 flex items-start justify-between gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Subscription</div>
                  <div className="font-display font-bold text-base mt-1">Subscription includes this title</div>
                  <p className="text-xs text-white/55 mt-1 max-w-md">
                    On = any subscriber on any active plan can watch. Off = only TVOD rent/buy or admin can watch.
                  </p>
                </div>
                <Switch checked={form.subscription_included} onCheckedChange={(v) => set("subscription_included", v)} data-testid="editor-subscription-included" />
              </div>
              {monetizationWarning && (
                <div className="border border-rose-400/30 bg-rose-500/5 p-3 flex items-start gap-2 text-xs text-rose-200" data-testid="editor-monetization-warning">
                  <Lock className="w-4 h-4 mt-0.5" />
                  <div>No-one can watch this. Either turn subscription on, or set a rent/buy price below.</div>
                </div>
              )}
              {!isNew && item ? (
                <PricingPanel content={item} onSaved={(updated) => setItem(updated)} tenant={tenant} />
              ) : (
                <div className="border border-dashed border-white/15 p-8 text-center text-white/45 text-sm">
                  Save the title first, then set rent/buy prices here.
                </div>
              )}
            </TabsContent>

            {/* Schedule */}
            <TabsContent value="schedule" className="space-y-5 mt-0">
              <Field label="Status">
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger className="bg-transparent border-white/15 rounded-none" data-testid="editor-status"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 rounded-none text-white">
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {form.status === "scheduled" && (
                <Field label="Publish at" hint="Stored as UTC. Published automatically once the time has passed (server tick on every public catalogue request).">
                  <Input
                    type="datetime-local"
                    value={form.publish_at}
                    onChange={(e) => set("publish_at", e.target.value)}
                    className="bg-transparent border-white/15 rounded-none focus-visible:border-white"
                    data-testid="editor-publish-at"
                  />
                </Field>
              )}
              <Field label="Featured on hero">
                <div className="flex items-center gap-3 h-10">
                  <Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="editor-featured" />
                  <span className="text-sm text-white/60">{form.featured ? "On hero rotation" : "Not featured"}</span>
                </div>
              </Field>
              <div className="text-[10px] text-white/35 uppercase tracking-[0.3em]">
                Hero ordering is set in catalogue → Reorder featured.
              </div>
            </TabsContent>
          </Tabs>

          {error && <div className="mt-5 text-sm text-red-400 border border-red-500/30 bg-red-500/5 p-3" data-testid="editor-error">{error}</div>}

          <div className="flex items-center justify-end pt-5 mt-5 border-t border-white/10">
            <Button type="submit" disabled={submitting} className="rounded-none bg-white text-black hover:bg-amber-300 uppercase text-[11px] tracking-[0.25em]" data-testid="editor-save">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (isNew ? "Create title" : "Save changes")}
            </Button>
          </div>
        </div>

        {/* Right rail preview */}
        <aside className="space-y-4">
          <div className="bg-[#121212] border border-white/10 p-5" data-testid="editor-preview">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">Card preview</div>
            <div className="aspect-video bg-black border border-white/10 overflow-hidden">
              {form.thumbnail_url ? (
                <img src={form.thumbnail_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              ) : (
                <div className="w-full h-full grid place-items-center text-white/30 text-xs">No thumbnail</div>
              )}
            </div>
            <div className="mt-3">
              <div className="font-display font-bold text-base">{form.title || "Title"}</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mt-1">
                {kind} · {form.release_year || "—"} · {form.age_rating}
              </div>
            </div>
          </div>
          {form.poster_portrait_url && (
            <div className="bg-[#121212] border border-white/10 p-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">Portrait poster</div>
              <div className="aspect-[2/3] bg-black border border-white/10 overflow-hidden max-w-[180px]">
                <img src={form.poster_portrait_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              </div>
            </div>
          )}
        </aside>
      </form>

      {!isNew && item?.kind === "series" && <EpisodesPanel seriesId={id} tenant={tenant} />}
      {!isNew && item && <InsightsDrawer open={insightsOpen} onOpenChange={setInsightsOpen} contentId={item.id} title={item.title} />}
    </StudioLayout>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-[0.3em] text-white/50">{label}</Label>
      {children}
      {hint && <div className="text-[10px] text-white/30">{hint}</div>}
    </div>
  );
}

function ArtField({ label, testid, aspect, value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const onFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const { data } = await api.post("/cms/images/direct-upload");
      const fd = new FormData();
      fd.append("file", file);
      const up = await fetch(data.upload_url, { method: "POST", body: fd });
      if (!up.ok) throw new Error("upload failed");
      onChange(data.delivery_url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error("Image upload failed — you can paste a URL instead");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_240px] gap-4 items-start">
      <Field label={label}>
        <div className="flex items-center gap-2">
          <label className="shrink-0 cursor-pointer inline-flex items-center gap-1.5 px-3 py-2 bg-white text-black text-[10px] font-semibold uppercase tracking-[0.15em] hover:bg-white/90" data-testid={`${testid}-upload`}>
            <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><UploadCloud className="w-3.5 h-3.5" /> Upload</>}
          </label>
          <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="…or paste an image URL" className="bg-transparent border-white/15 rounded-none focus-visible:border-white font-mono text-sm" data-testid={testid} />
        </div>
      </Field>
      <div className={`${aspect} bg-black border border-white/10 overflow-hidden`}>
        {value ? (
          <img src={value} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          <div className="w-full h-full grid place-items-center text-white/30 text-[10px] uppercase tracking-widest">Preview</div>
        )}
      </div>
    </div>
  );
}

function SubtitlesEditor({ item, onChanged }) {
  const [tracks, setTracks] = useState(item.subtitles || []);
  const [language, setLanguage] = useState("en");
  const [labelText, setLabel] = useState("English");
  const [vttUrl, setVttUrl] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setTracks(item.subtitles || []); }, [item]);

  const add = async () => {
    if (!vttUrl.trim()) {
      toast.error("Paste a .vtt URL");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/cms/content/${item.id}/subtitles`, {
        language: language.trim(), label: labelText.trim(), vtt_url: vttUrl.trim(),
      });
      onChanged(data);
      setVttUrl("");
      toast.success("Subtitle track added");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (idx) => {
    if (!window.confirm("Remove this subtitle track?")) return;
    try {
      const { data } = await api.delete(`/cms/content/${item.id}/subtitles/${idx}`);
      onChanged(data);
      toast.success("Track removed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-4" data-testid="subtitles-editor">
      <div className="border border-white/10 bg-black/30 p-3 flex items-start gap-3">
        <Subtitles className="w-4 h-4 mt-0.5 text-white/45" />
        <p className="text-xs text-white/55 font-light">Paste a public .vtt URL per language. Files must be served with proper CORS headers.</p>
      </div>
      {tracks.length === 0 ? (
        <div className="border border-dashed border-white/15 p-8 text-center text-white/45 text-sm">No subtitle tracks yet.</div>
      ) : (
        <div className="border border-white/10 divide-y divide-white/10">
          {tracks.map((t, idx) => (
            <div key={idx} className="flex items-center gap-3 p-3 text-sm" data-testid={`subtitle-row-${idx}`}>
              <div className="font-mono text-xs text-white/40 w-12">{t.language}</div>
              <div className="flex-1 min-w-0">
                <div>{t.label}</div>
                <div className="text-[11px] text-white/40 truncate">{t.vtt_url}</div>
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-300 hover:text-rose-200" onClick={() => remove(idx)} data-testid={`subtitle-remove-${idx}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
      <div className="border border-white/10 p-4 grid grid-cols-1 md:grid-cols-[120px_1fr_1fr_auto] gap-3">
        <Input value={language} onChange={(e) => setLanguage(e.target.value)} placeholder="lang (en/hi/es)" className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="subtitle-lang-input" />
        <Input value={labelText} onChange={(e) => setLabel(e.target.value)} placeholder="Display label" className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="subtitle-label-input" />
        <Input value={vttUrl} onChange={(e) => setVttUrl(e.target.value)} placeholder="https://.../subs.vtt" className="bg-transparent border-white/15 rounded-none focus-visible:border-white font-mono text-xs" data-testid="subtitle-url-input" />
        <Button type="button" onClick={add} disabled={busy} className="rounded-none bg-white text-black hover:bg-amber-300 uppercase text-[11px] tracking-[0.25em]" data-testid="subtitle-add-button">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add track"}
        </Button>
      </div>
    </div>
  );
}

function InsightsDrawer({ open, onOpenChange, contentId, title }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !contentId) return;
    setLoading(true);
    api.get(`/cms/content/${contentId}/insights`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, [open, contentId]);

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="bg-[#0a0a0a] text-white border-white/10" data-testid="insights-drawer">
        <DrawerHeader>
          <DrawerTitle className="font-display tracking-tight">Insights · {title}</DrawerTitle>
          <DrawerDescription className="text-white/55">Last 30 days</DrawerDescription>
        </DrawerHeader>
        <div className="px-4 pb-4">
          {loading ? (
            <div className="text-center text-white/40 text-xs uppercase tracking-widest py-12">Loading…</div>
          ) : !data ? (
            <div className="text-center text-white/40 text-xs uppercase tracking-widest py-12">No data</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl mx-auto" data-testid="insights-kpis">
              <Kpi label="Views" value={data.views_30d} />
              <Kpi label="Watch time" value={`${Math.round((data.watch_seconds_30d || 0) / 60)}m`} />
              <Kpi label="Completion" value={`${Math.round(data.completion_rate * 100)}%`} />
              <Kpi label="Paywall views" value={data.paywall_views_30d} />
              <Kpi label="Conversions" value={data.conversion_count} />
              <Kpi label="Conv rate" value={`${Math.round(data.conversion_rate * 100)}%`} />
            </div>
          )}
          {data?.recent_viewers?.length > 0 && (
            <div className="mt-6 max-w-3xl mx-auto">
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Recent viewers</div>
              <div className="border border-white/10 bg-[#121212] divide-y divide-white/10">
                {data.recent_viewers.map((u, i) => (
                  <div key={i} className="px-3 py-2 text-xs">{u.email || "(anonymous)"}</div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DrawerFooter>
          <DrawerClose asChild>
            <Button variant="ghost" className="rounded-none uppercase text-[11px] tracking-[0.25em]">Close</Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="border border-white/10 bg-[#121212] p-4">
      <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">{label}</div>
      <div className="font-display font-bold text-2xl mt-1">{value}</div>
    </div>
  );
}

// ----- Episodes Panel (only for series in edit mode) -----
function EpisodesPanel({ seriesId, tenant }) {
  const [eps, setEps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/cms/series/${seriesId}/episodes`);
      setEps(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [seriesId]);

  const onDelete = async (ep) => {
    if (!window.confirm(`Delete S${ep.season_number}E${ep.episode_number} "${ep.title}"?`)) return;
    try {
      await api.delete(`/cms/episodes/${ep.id}`);
      toast.success("Episode deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="mt-10">
      <div className="flex items-end justify-between mb-4">
        <h2 className="font-display font-bold text-2xl tracking-tight">Episodes</h2>
        <Button onClick={() => setCreating(true)} className="rounded-none bg-white text-black hover:bg-amber-300 uppercase text-[11px] tracking-[0.25em]" data-testid="episode-add-button">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Add episode
        </Button>
      </div>
      <div className="border border-white/10 bg-[#121212]">
        {loading ? (
          <div className="p-8 text-center text-white/40 text-xs uppercase tracking-widest">Loading…</div>
        ) : eps.length === 0 ? (
          <div className="p-8 text-center text-white/40 text-xs uppercase tracking-widest">No episodes yet</div>
        ) : (
          <div className="divide-y divide-white/10">
            {eps.map((ep) => (
              <div key={ep.id} className="flex items-center gap-4 p-4 hover:bg-white/5" data-testid={`episode-row-${ep.id}`}>
                <div className="font-mono text-xs text-white/50 w-16">S{String(ep.season_number).padStart(2,"0")}E{String(ep.episode_number).padStart(2,"0")}</div>
                <div className="w-24 h-14 bg-black/40 border border-white/10 overflow-hidden flex-shrink-0">
                  {ep.thumbnail_url && <img src={ep.thumbnail_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{ep.title}</div>
                  <div className="text-[11px] text-white/40 truncate">{ep.description}</div>
                </div>
                <div className="text-xs text-white/50 hidden md:block">{ep.duration_minutes ? `${ep.duration_minutes} min` : "—"}</div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 hidden md:block">{ep.video_source?.type}</div>
                <Button variant="ghost" size="icon" title="Edit episode / replace video" className="h-8 w-8 text-white/60 hover:text-white" onClick={() => setEditing(ep)} data-testid={`episode-edit-${ep.id}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" title="Delete episode (removes its views & analytics)" className="h-8 w-8 text-red-300 hover:text-red-200" onClick={() => onDelete(ep)} data-testid={`episode-delete-${ep.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {(creating || editing) && (
        <EpisodeForm
          seriesId={seriesId}
          episode={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function EpisodeForm({ seriesId, episode, onClose, onSaved }) {
  const isEdit = !!episode;
  const [form, setForm] = useState({
    season_number: episode?.season_number ?? 1,
    episode_number: episode?.episode_number ?? 1,
    title: episode?.title ?? "",
    description: episode?.description ?? "",
    thumbnail_url: episode?.thumbnail_url ?? "",
    duration_minutes: episode?.duration_minutes ?? "",
    video_url: episode?.video_source?.type === "cloudflare" ? "" : (episode?.video_source?.original_url ?? ""),
    cf_uid: episode?.video_source?.type === "cloudflare" ? (episode?.video_source?.uid ?? "") : "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        season_number: parseInt(form.season_number, 10),
        episode_number: parseInt(form.episode_number, 10),
        title: form.title.trim(),
        description: form.description,
        thumbnail_url: form.thumbnail_url.trim(),
        duration_minutes: form.duration_minutes !== "" ? parseInt(form.duration_minutes, 10) : null,
      };
      if (form.cf_uid) payload.cf_uid = form.cf_uid;
      else payload.video_url = form.video_url.trim();
      if (isEdit) {
        await api.patch(`/cms/episodes/${episode.id}`, payload);
        toast.success("Episode saved");
      } else {
        await api.post(`/cms/series/${seriesId}/episodes`, payload);
        toast.success("Episode created");
      }
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" onClick={onClose}>
      <div className="bg-[#0d0d0d] border border-white/10 max-w-xl w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="episode-form">
        <div className="font-display font-bold text-2xl tracking-tight mb-4">{isEdit ? "Edit episode" : "Add episode"}</div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Season">
              <Input type="number" min={1} value={form.season_number} onChange={(e) => set("season_number", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="episode-season" />
            </Field>
            <Field label="Episode">
              <Input type="number" min={1} value={form.episode_number} onChange={(e) => set("episode_number", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="episode-number" />
            </Field>
          </div>
          <Field label="Title">
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} required className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="episode-title" />
          </Field>
          <Field label="Description">
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="episode-description" />
          </Field>
          <Field label="Episode video" hint="Upload the episode file — streamed via Cloudflare.">
            <StreamUploader
              cfUid={form.cf_uid}
              onUploaded={(uid) => { set("cf_uid", uid); set("video_url", ""); }}
              onClear={() => set("cf_uid", "")}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Thumbnail URL">
              <Input value={form.thumbnail_url} onChange={(e) => set("thumbnail_url", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white font-mono text-sm" data-testid="episode-thumb" />
            </Field>
            <Field label="Duration (min)">
              <Input type="number" value={form.duration_minutes} onChange={(e) => set("duration_minutes", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="episode-duration" />
            </Field>
          </div>
          {error && <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 p-3">{error}</div>}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10">
            <Button type="button" variant="ghost" onClick={onClose} className="rounded-none uppercase text-[11px] tracking-[0.25em] text-white/60 hover:text-white">Cancel</Button>
            <Button type="submit" disabled={submitting} className="rounded-none bg-white text-black hover:bg-amber-300 uppercase text-[11px] tracking-[0.25em]" data-testid="episode-save">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ----- Pricing Panel (TVOD rent + buy) -----
function PricingPanel({ content, tenant, onSaved }) {
  const defaultCurrency = tenant?.default_currency || "INR";
  const [rent, setRent] = useState(content.tvod_rent_price ? smallestUnitToMajor(content.tvod_rent_price.amount) : "");
  const [rentCurrency, setRentCurrency] = useState(content.tvod_rent_price?.currency || defaultCurrency);
  const [buy, setBuy] = useState(content.tvod_buy_price ? smallestUnitToMajor(content.tvod_buy_price.amount) : "");
  const [buyCurrency, setBuyCurrency] = useState(content.tvod_buy_price?.currency || defaultCurrency);
  const [submitting, setSubmitting] = useState(false);

  const onSave = async () => {
    setSubmitting(true);
    try {
      const body = {};
      if (rent === "" || rent === null) body.clear_rent = true;
      else body.tvod_rent_price = { amount: majorToSmallestUnit(rent), currency: rentCurrency };
      if (buy === "" || buy === null) body.clear_buy = true;
      else body.tvod_buy_price = { amount: majorToSmallestUnit(buy), currency: buyCurrency };
      const { data } = await api.patch(`/cms/content/${content.id}/pricing`, body);
      toast.success("Pricing saved");
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-2 border border-white/10 bg-[#0a0a0a] p-5" data-testid="pricing-panel">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h3 className="font-display font-bold text-lg tracking-tight">TVOD pricing</h3>
          <p className="text-white/50 text-xs mt-1">Per-title rent / buy. Subscriptions still grant access.</p>
        </div>
        <Button onClick={onSave} disabled={submitting} className="rounded-none bg-white text-black hover:bg-amber-300 uppercase text-[11px] tracking-[0.25em]" data-testid="pricing-save">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save pricing"}
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-[#121212] border border-white/10 p-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Rent · 48h access</div>
          <div className="flex items-center gap-2">
            <span className="text-xl text-white/60 w-6">{currencySymbol(rentCurrency)}</span>
            <Input type="number" step="0.01" min={0} value={rent} onChange={(e) => setRent(e.target.value)} placeholder="(none)" className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="pricing-rent" />
            <Select value={rentCurrency} onValueChange={setRentCurrency}>
              <SelectTrigger className="bg-transparent border-white/15 rounded-none w-24" data-testid="pricing-rent-currency"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10 rounded-none text-white">
                <SelectItem value="INR">INR</SelectItem>
                <SelectItem value="USD">USD</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="text-[10px] text-white/30 mt-2">
            {content.tvod_rent_price ? `Currently · ${formatMoney(content.tvod_rent_price.amount, content.tvod_rent_price.currency)}` : "No rent price set"}
          </div>
        </div>
        <div className="bg-[#121212] border border-white/10 p-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Buy · own forever</div>
          <div className="flex items-center gap-2">
            <span className="text-xl text-white/60 w-6">{currencySymbol(buyCurrency)}</span>
            <Input type="number" step="0.01" min={0} value={buy} onChange={(e) => setBuy(e.target.value)} placeholder="(none)" className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="pricing-buy" />
            <Select value={buyCurrency} onValueChange={setBuyCurrency}>
              <SelectTrigger className="bg-transparent border-white/15 rounded-none w-24" data-testid="pricing-buy-currency"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10 rounded-none text-white">
                <SelectItem value="INR">INR</SelectItem>
                <SelectItem value="USD">USD</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="text-[10px] text-white/30 mt-2">
            {content.tvod_buy_price ? `Currently · ${formatMoney(content.tvod_buy_price.amount, content.tvod_buy_price.currency)}` : "No buy price set"}
          </div>
        </div>
      </div>
      <div className="mt-3 text-[10px] uppercase tracking-[0.3em] text-white/30">Tip · clear a price by emptying the field and saving.</div>
    </div>
  );
}
